import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyMoodDashboard } from './companyMoodApi';
import ContextMixPanel from './ContextMixPanel';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { BAND_RAMP, FREQUENCY_RAMP, DAY_PART_RAMP } from './moodTheme';

/**
 * Mood Tracking → Dashboard.
 *
 * This reads the Mood Log check-in — an emotion, an optional second emotion,
 * and the context around it (doing / with / where). The legacy slider moods
 * that feed the main B2B dashboard's positivity widgets are a different record
 * entirely and never appear here; the two cannot be summed.
 *
 * Three things carry the page:
 *
 *  1. The BAND MIX. Every emotion in the taxonomy is green, amber or red, and a
 *     check-in takes the band of its primary emotion — so Positive / Mixed /
 *     Difficult always sums to the check-in count and is directly comparable
 *     between two companies of different sizes.
 *  2. The CONTEXT PANELS, which are the reason this section is worth building.
 *     Each shows the band mix normalised within one context option, so "the
 *     difficult share at Work is double the one at Home" becomes visible —
 *     something no volume chart can show.
 *  3. LOCAL TIME. Unlike every other section, the check-in record carries the
 *     user's own wall clock, so the hour and weekday charts here are genuinely
 *     local rather than UTC.
 *
 * Nothing a person wrote or photographed is on this page or reachable from it:
 * the note is encrypted special-category health data and is never decrypted for
 * the panel.
 *
 * `fetcher` is a prop so a super-admin Mood hub can render this page unscoped;
 * the builders behind both are the same.
 */
const MoodDashboard = ({ fetcher = fetchCompanyMoodDashboard, populationLabel = 'users' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const bandSplit = stats?.bandSplit || [];
  const frequency = stats?.frequency || [];
  const dayParts = stats?.dayParts || [];
  const weekdayCheckIns = stats?.weekdayCheckIns || [];
  const hourlyCheckIns = stats?.hourlyCheckIns || [];
  const positiveEmotions = stats?.positiveEmotions || [];
  const difficultEmotions = stats?.difficultEmotions || [];
  const positiveTrend = stats?.positiveTrend || [];
  const mixedTrend = stats?.mixedTrend || [];
  const difficultTrend = stats?.difficultTrend || [];

  // What share of the population has ever logged a mood.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.loggers / stats.totalUsers) * 100) : null;

  const trendHasData = positiveTrend.some((val, i) => val + mixedTrend[i] + difficultTrend[i] > 0);

  /**
   * Thirty days of check-ins, stacked by band. Stacked rather than three lines
   * because the total height is the volume and the colour split is the mood —
   * both readings matter, and a run of red growing while the bars stay the same
   * height is exactly the pattern worth catching.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true },
    colors: BAND_RAMP,
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(positiveTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true, y: { formatter: (val) => `${val.toLocaleString()} check-ins` } }
  };

  /**
   * Check-ins by hour of the user's own clock. A hump late at night is a
   * wellbeing signal that a daily total hides completely — and unlike the other
   * sections this really is local time, not UTC.
   */
  const hourlyOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: hourlyCheckIns.map((slot) => slot.label), tickAmount: 8 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} check-ins` } }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayCheckIns.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} check-ins` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Tracking"
          value={stats ? formatBigNumber(stats.loggers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Check-ins"
          value={stats ? formatBigNumber(stats.checkIns) : '-'}
        />
        {/* The two ends of the band mix. Shown as a pair on purpose: either one
            alone invites the wrong conclusion, since "Mixed" sits between them
            and moves independently. */}
        <StatCard
          icon={STAT_ICONS.rating}
          title="Positive Moods"
          value={stats?.checkIns > 0 ? `${stats.positiveRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Difficult Moods"
          value={stats?.checkIns > 0 ? `${stats.difficultRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Logged"
          value={stats ? formatBigNumber(stats.loggedDays) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Check-ins per Person"
          value={stats ? stats.avgCheckInsPerUser : '-'}
        />
      </div>

      {/* ---------- Mix, consistency, timing ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Mood Balance"
          subtitle="Every check-in, by emotion band"
          data={bandSplit}
          colors={BAND_RAMP}
          unit="check-ins"
          totalLabel="Check-ins"
        />
        <MiniDonutCard
          title="How Often People Log"
          subtitle="Check-ins per person"
          data={frequency}
          colors={FREQUENCY_RAMP}
          unit="people"
          totalLabel="People"
        />
        <MiniDonutCard
          title="Time of Day"
          subtitle="When people log, in their own timezone"
          data={dayParts}
          colors={DAY_PART_RAMP}
          unit="check-ins"
          totalLabel="Check-ins"
          legendLimit={4}
        />
      </div>

      {/* ---------- Trend and timing ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Mood Over Time" subtitle="Check-ins by band — last 30 days">
          {trendHasData ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[
                { name: 'Positive', data: positiveTrend },
                { name: 'Mixed', data: mixedTrend },
                { name: 'Difficult', data: difficultTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No check-ins in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="When People Check In" subtitle="By hour of day, in the user's own timezone">
          {hourlyCheckIns.some((slot) => slot.count > 0) ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'Check-ins', data: hourlyCheckIns.map((slot) => slot.count) }]}
              options={hourlyOptions}
            />
          ) : (
            <PanelEmpty>No check-ins recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Named emotions ----------
          Ranked by how many PEOPLE named the emotion, not by how many times it
          was named, so one prolific logger cannot invent a company-wide feeling.
          Both a primary and a secondary emotion count, matching how the app's
          own weekly review counts them. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title="What's Lifting People"
          subtitle={`Positive emotions named across your ${populationLabel}`}
        >
          {positiveEmotions.length > 0 ? (
            <ProgressList items={positiveEmotions} unit="people" color={CHART_COLORS.green} />
          ) : (
            <PanelEmpty>No emotions logged yet.</PanelEmpty>
          )}
        </Panel>

        <Panel
          title="What's Weighing on People"
          subtitle={`Mixed and difficult emotions named across your ${populationLabel}`}
        >
          {difficultEmotions.length > 0 ? (
            <ProgressList items={difficultEmotions} unit="people" color={CHART_COLORS.red} />
          ) : (
            <PanelEmpty>No emotions logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Context ----------
          The three panels the section exists for. Each row is normalised to its
          own total, so a rare context and a common one can be compared. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <ContextMixPanel
          title="What They're Doing"
          subtitle="Mood mix by activity"
          rows={stats?.byActivity || []}
        />
        <ContextMixPanel
          title="Where They Are"
          subtitle="Mood mix by location"
          rows={stats?.byLocation || []}
        />
        <ContextMixPanel
          title="Who They're With"
          subtitle="Mood mix by company"
          rows={stats?.byCompanion || []}
        />
      </div>

      {/* ---------- Reach and supporting detail ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Mood Tracking Reach"
          subtitle={`Share of ${populationLabel} who log their mood`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.loggers)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Check-ins by Weekday" subtitle="Which days people stop and log">
          {weekdayCheckIns.some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Check-ins', data: weekdayCheckIns.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No check-ins recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Check-in Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Days logged per person</td>
              <td className="py-2 text-right">{stats ? stats.avgDaysPerUser : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Check-ins per day logged</td>
              <td className="py-2 text-right">{stats ? stats.avgCheckInsPerDay : '-'}</td>
            </tr>
            {/* How much of the 53-emotion taxonomy is actually in use — a
                company naming five emotions is using a different tool from one
                naming forty. */}
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Emotions in use</td>
              <td className="py-2 text-right">
                {stats ? `${stats.emotionsNamed} of ${stats.emotionsAvailable}` : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Named two emotions</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.withSecondEmotion) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Weekly reviews generated</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.weeklyReviews) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last check-in</td>
              <td className="py-2 text-right">{stats?.lastCheckInDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

MoodDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default MoodDashboard;
